/**
 * Open a URL that has to be fetched first (a signed download link) in a new
 * tab without losing the user's tap (de-slop CHAT-13).
 *
 * `await api(); window.open(url)` runs after the transient user activation
 * has expired, so iOS Safari and popup blockers silently drop the window.
 * Here the blank window is opened synchronously in the tap handler and
 * pointed at the URL once it arrives. Call it directly from the press
 * handler, before any `await`.
 */
export interface WebDownloadEnv {
  open: (url: string, target: string) => Window | null;
  location: { assign: (url: string) => void };
}

export async function openFetchedUrlInNewTab(
  getUrl: () => Promise<string | null | undefined>,
  env: WebDownloadEnv = window,
): Promise<void> {
  const win = env.open('', '_blank');
  try {
    const url = await getUrl();
    if (!url) {
      win?.close();
      return;
    }
    if (win && !win.closed) {
      try {
        win.opener = null;
      } catch {
        // Some browsers make `opener` read-only; the navigation still works.
      }
      win.location.href = url;
    } else {
      // The browser refused even the synchronous window: use this tab.
      env.location.assign(url);
    }
  } catch (error) {
    win?.close();
    throw error;
  }
}
